import { call, put, select, takeLatest, takeEvery, all } from 'redux-saga/effects';
import tzlookup from 'tz-lookup';
import { ForbiddenError, NotFoundError } from '@iq/services';

import {
  getAllSitesData,
  getModelThumbnail as getModelThumbnailApi,
  createSite as createSiteApi,
  updateSite as updateSiteApi,
  deleteSite as deleteSiteApi,
  uploadSitesExcel as uploadSitesExcelApi,
  downloadSitesExcelTemplate as downloadSitesExcelTemplateApi,
} from '../../services';
import { downloadBlob } from '../../utils';
import { pollForSiteComponent } from '../components';
import { pollForInternalSource } from '../sources';
import { displayNotification, checkOnline, getNotification } from '../notifications';
import { setTimezone } from '../application';
import {
  GET_SITES,
  CREATE_SITE,
  SET_ACTIVE_SITE,
  UPDATE_SITE,
  DELETE_SITE,
  DOWNLOAD_SITES_TEMPLATE,
  UPLOAD_SITES_EXCEL_SHEET,
  REQUEST_MODEL_THUMBNAIL,
  setSites,
  setActiveSiteId,
  setCreatedSite,
  receiveModelThumbnail,
  removeSite,
  requestSites,
} from './actions';
import { getSite } from './selectors';

export function* doSetActiveSite({ siteId }) {
  try {
    const site = yield select((state) => getSite(state, siteId));
    if (site) {
      const {
        location: { lat, lng },
      } = site;
      let tz = 'Europe/Stockholm';
      try {
        tz = tzlookup(lat, lng);
      } catch (e) {
        console.error(e);
      }
      yield put(setTimezone(tz));
      yield put(setActiveSiteId(site.id));
    }
  } catch (e) {
    console.error('Unable to set active site: ', e);
    yield call(checkOnline);
  }
}

export function* doRequestSitesData({ callback = () => null }) {
  try {
    const { values } = yield call(getAllSitesData);
    const sites = values.filter(({ location }) => !!location);
    yield put(setSites(sites));
  } catch (e) {
    console.error('Unable to fetch site data: ', e);
    yield call(checkOnline);
    yield put(setSites([]));
    if (e instanceof ForbiddenError) {
      if (callback) {
        yield callback();
      }
      return;
    }
    yield put(displayNotification(getNotification('getSites', 'error')()));
  }

  if (callback) {
    yield callback();
  }
}

export function* doRequestModelThumbnail({ modelId, versionId }) {
  try {
    const data = yield call(getModelThumbnailApi, modelId, versionId);
    const reader = new FileReader();
    const src = yield new Promise((resolve) => {
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(data);
    });
    yield put(receiveModelThumbnail(modelId, versionId, src));
  } catch (e) {
    if (!(e instanceof NotFoundError)) {
      yield call(checkOnline);
      console.error('Unable to fetch model thumbnail: ', e);
    }
    // we add a string 'none' to indicate that the thumbnail was not found or an error occurred
    yield put(receiveModelThumbnail(modelId, versionId, 'none'));
  }
}

export function* doCreateSite(action) {
  const { site, callback } = action;
  try {
    const createdSite = yield call(createSiteApi, site);
    if (createdSite) {
      const { id: siteId } = createdSite;
      const { values } = yield call(getAllSitesData);
      const sites = values.filter(({ location }) => !!location);
      yield put(setSites(sites));
      yield all([call(pollForInternalSource, siteId), call(pollForSiteComponent, siteId)]);
      yield put(setCreatedSite(createdSite));
    }
  } catch (e) {
    console.error('Encountered an error while creating site: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('createSite', 'error')()));
  }
  callback();
}

export function* doUpdateSite(action) {
  const { siteId, site, callback = () => null } = action;
  try {
    const updatedSite = yield call(updateSiteApi, siteId, site);
    yield put(displayNotification(getNotification('updateSite', 'success')(siteId)));
    if (updatedSite) {
      const { values: sites } = yield call(getAllSitesData);
      yield put(setSites(sites));
      if (callback) {
        yield callback();
      }
    }
  } catch (e) {
    console.error('Unable to update site: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('updateSite', 'error')(siteId)));
  }
}

export function* doDeleteSite(action) {
  const { siteId } = action;
  try {
    yield call(deleteSiteApi, siteId);
    yield put(removeSite(siteId));
    yield put(displayNotification(getNotification('deleteSite', 'success')(siteId)));
  } catch (e) {
    console.error('Unable to delete site: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('deleteSite', 'error')(siteId)));
  }
}

export function* doUploadSitesExcel(action) {
  const { file, realm } = action;
  try {
    const fd = new FormData();
    fd.append('sites', file);
    fd.append('org', realm);
    yield call(uploadSitesExcelApi, fd);
    yield put(requestSites());
    yield put(displayNotification(getNotification('importSites', 'success')()));
  } catch (e) {
    console.error('Unable to import sites: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('importSites', 'error')(e.message)));
  }
}

export function* doDownloadSitesTemplate() {
  try {
    const file = yield call(downloadSitesExcelTemplateApi);
    downloadBlob(file.data, 'sites_config_template.xlsx', file.headers['content-type']);
  } catch (e) {
    console.error('Unable to download sites template: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('exportSites', 'error')()));
  }
}

export const sagas = [
  takeLatest(GET_SITES, doRequestSitesData),
  takeLatest(SET_ACTIVE_SITE, doSetActiveSite),
  takeLatest(CREATE_SITE, doCreateSite),
  takeLatest(UPDATE_SITE, doUpdateSite),
  takeLatest(DELETE_SITE, doDeleteSite),
  takeLatest(DOWNLOAD_SITES_TEMPLATE, doDownloadSitesTemplate),
  takeLatest(UPLOAD_SITES_EXCEL_SHEET, doUploadSitesExcel),
  takeEvery(REQUEST_MODEL_THUMBNAIL, doRequestModelThumbnail),
];
